import { useCallback, useMemo } from "react";
import {
  buildLogoAlt,
  getTenantFaviconUrl,
  getTenantLogoUrl,
  PLATFORM_FAVICON_SRC,
  PLATFORM_LOGO_SRC,
  PLATFORM_SITE_NAME,
  type TenantLogoVariant,
} from "@/utils";
import { useTenant } from "./useTenant";

interface UseTenantBrandReturn {
  isBranded: boolean;
  siteName: string;
  logoAlt: string;
  getLogoSrc: (variant: TenantLogoVariant) => string;
  faviconSrc: string;
}

/**
 * Resolves tenant-specific branding assets or platform defaults.
 */
export const useTenantBrand = (): UseTenantBrandReturn => {
  const {
    isTenantSite,
    effectiveTenantSlug,
    space,
    tenant,
    loading,
    error,
  } = useTenant();

  const usePlatformAssets =
    !isTenantSite || (!loading && (Boolean(error) || !space));

  const isBranded = isTenantSite && !usePlatformAssets && Boolean(space);

  const siteName = !isTenantSite
    ? PLATFORM_SITE_NAME
    : (space?.name ?? tenant?.space?.name ?? PLATFORM_SITE_NAME);

  const logoAlt = buildLogoAlt(siteName);

  const getLogoSrc = useCallback(
    (variant: TenantLogoVariant): string =>
      usePlatformAssets
        ? PLATFORM_LOGO_SRC
        : getTenantLogoUrl(effectiveTenantSlug, variant),
    [effectiveTenantSlug, usePlatformAssets],
  );

  const faviconSrc = useMemo(
    () =>
      usePlatformAssets
        ? PLATFORM_FAVICON_SRC
        : getTenantFaviconUrl(effectiveTenantSlug),
    [effectiveTenantSlug, usePlatformAssets],
  );

  return {
    isBranded,
    siteName,
    logoAlt,
    getLogoSrc,
    faviconSrc,
  };
};
